import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { ChevronLeft, ChevronRight } from "lucide-react";

const dateKey = (b) => (b.start_date || b.details?.date || b.created_at || "").slice(0, 10);

export const BookingCalendar = ({ bookings }) => {
  const { t, lang } = useApp();
  const [cur, setCur] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(null);

  const byDay = useMemo(() => {
    const m = {};
    bookings.forEach((b) => { const k = dateKey(b); if (k) (m[k] = m[k] || []).push(b); });
    return m;
  }, [bookings]);

  const y = cur.getFullYear(), mo = cur.getMonth();
  const first = new Date(y, mo, 1).getDay();
  const days = new Date(y, mo + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const monthLabel = cur.toLocaleDateString(lang === "ar" ? "ar-IQ" : "en-US", { month: "long", year: "numeric" });
  const weekdays = lang === "ar" ? ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"] : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const key = (d) => `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const todayKey = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="booking-calendar">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="cal-prev" onClick={() => setCur(new Date(y, mo - 1, 1))} className="p-2 rounded-lg hover:bg-slate-100"><ChevronLeft size={18} className="rtl:rotate-180" /></button>
        <h3 className="font-bold text-slate-800" data-testid="cal-month">{monthLabel}</h3>
        <button data-testid="cal-next" onClick={() => setCur(new Date(y, mo + 1, 1))} className="p-2 rounded-lg hover:bg-slate-100"><ChevronRight size={18} className="rtl:rotate-180" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-slate-400 mb-1">
        {weekdays.map((w) => <div key={w}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const k = key(d), items = byDay[k] || [];
          const has = items.length > 0;
          return (
            <button key={k} data-testid={`cal-day-${k}`} onClick={() => setSelected(has ? k : null)}
              className={`aspect-square rounded-lg text-sm flex flex-col items-center justify-center gap-0.5 transition-colors
                ${has ? "bg-tigris text-white font-bold hover:bg-tigris-dark" : "text-slate-600 hover:bg-slate-50"}
                ${k === todayKey && !has ? "ring-1 ring-gold" : ""} ${selected === k ? "ring-2 ring-gold" : ""}`}>
              {d}
              {has && <span className="w-1.5 h-1.5 rounded-full bg-gold" />}
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="mt-4 border-t border-slate-100 pt-3 space-y-2" data-testid="cal-selected-list">
          {(byDay[selected] || []).map((b) => (
            <Link key={b.ref} to={`/booking/${b.ref}`} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-slate-50 hover:bg-tigris/5">
              <span className="font-semibold text-slate-700 truncate">{b.title}</span>
              <span className="font-mono text-xs text-tigris">{b.ref}</span>
            </Link>
          ))}
        </div>
      )}
      {!bookings.length && <p className="text-xs text-slate-400 text-center mt-3">{t.dashboard.empty}</p>}
    </div>
  );
};
